import {FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {ChangeDetectionStrategy, Component, Inject, Injectable, signal} from "@angular/core";
import {lyttTilEsc} from "felles-ng2/støttefunksjoner/tastaturhendelser";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MeldingTilBruker} from "felles/tjenester/MeldingTilBruker";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {EnkelGruppeModell, GruppeMedlemListeModell, MedlemId, MedlemsRollePeriode2Modell, MedlemStengTilgangSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {GjeldendeTidssone} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {skrivPlainDateSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import {Temporal} from "temporal-polyfill";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {TranslateModule} from "@ngx-translate/core";
import {MatInputModule} from "@angular/material/input";
import {MatButtonModule} from "@angular/material/button";
import {MedlemStengTilgangBekreftelseKomponent} from "../../komponenter/medlem-steng-tilgang-bekreftelse/medlem-steng-tilgang-bekreftelse";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {lagrerSignal} from "felles-ng2/tjenester/LagrerIndikator";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {lesDatoPeriodeUtenTid} from "felles-ng2/støttefunksjoner/plainDatePerioder";
import PlainDate = Temporal.PlainDate;
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";

const debug = initDebugger('MedlemStengTilgangDialogKomponent');

export type MedlemStengTilgangDialogRespons = boolean;
export type RollePeriodeSkjemaType = ReturnType<typeof rolleperiodeSkjema>;
export type RollePeriodeSkjemaVerdi = RollePeriodeSkjemaType['value'];

@Component({
    selector: 'medlem-stengtilgang-dialog',
    templateUrl: 'medlem-stengtilgang-dialog.html',
    styleUrl: 'medlem-stengtilgang-dialog.scss',
    host: { 'class': 'e2e-stengmedlemtilgangdialog' },
    imports: [
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatInputModule,
        MatFormFieldModule,
        MatDatepickerModule,
        MatCheckboxModule,
        TranslateModule,
        FormaterPlainDatePipe,
        StandardValideringsfeilPipe,
        MedlemStengTilgangBekreftelseKomponent,
        VisSpinnerDirektiv
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedlemStengTilgangDialogKomponent {

    static vis(dialog: MatDialog, medlem: GruppeMedlemListeModell, gruppe: EnkelGruppeModell): MatDialogRef<MedlemStengTilgangDialogKomponent, MedlemStengTilgangDialogRespons> {
        return dialog.open(MedlemStengTilgangDialogKomponent, {data: {medlem, gruppe} as DialogParametre, disableClose: true, ariaLabelledBy: 'medlem-stengtilgang-dialog-tittel', minHeight: 400, maxWidth: '600px'});
    }

    medlem!: GruppeMedlemListeModell;
    gruppe!: EnkelGruppeModell;

    aktiveRollerSkjema = new FormArray<RollePeriodeSkjemaType>([])
    trinn = signal<'avsluttRoller' | 'stengTilgang'>('avsluttRoller');
    lagrer = lagrerSignal();

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private avhengigheter: MedlemStengTilgangDialogKomponentAvhengigheter,
                private gjeldendeTidssone: GjeldendeTidssone,
                private meldingTilBruker: MeldingTilBruker,
                private oversettelser: Oversettelser,
                private klokke: Klokke,
                public dialogRef: MatDialogRef<MedlemStengTilgangDialogKomponent, MedlemStengTilgangDialogRespons>) {
        //dialog er konf med disableClose:true for å unngå at klikk utenfor dialog lukker denne, ønsker fortsatt at esc skal lukke
        lyttTilEsc(() => this.avbryt());

        this.medlem = parametre.medlem;
        this.gruppe = parametre.gruppe;

        this.medlem.aktiveMedlemsroller
          .map(rolle => this.somRolleperiodeskjema(rolle))
          .forEach(linje => this.aktiveRollerSkjema.push(linje));

        if (this.aktiveRollerSkjema.length == 0) this.trinn.set('stengTilgang');
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    async steng() {
        await this.lagrer.utfør(async () => {
            await this.avhengigheter.fjernTilgang(this.medlem.id, this.somSpesifikasjon());
            this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Tilgang til gruppe stengt for {navnEllerEpost}'), {navnEllerEpost: this.medlem.navn || this.medlem.epost}));
            this.dialogRef.close(true);
        });
    }

    //hjelpemetoder

    private somRolleperiodeskjema(rolle: MedlemsRollePeriode2Modell): RollePeriodeSkjemaType {
        const skjema = rolleperiodeSkjema(rolle);
        skjema.controls.avslutt.valueChanges
          .pipe(takeUntilDestroyed())
          .subscribe(avslutt => this.settSluttdatoDersomAvsluttet(avslutt!, skjema));
        return skjema;
    }

    private settSluttdatoDersomAvsluttet(avsluttet: boolean, linje: RollePeriodeSkjemaType) {
        if (avsluttet) linje.controls.sluttdato.enable();
        else linje.controls.sluttdato.disable();

        if (!avsluttet || erIkkeNil(linje.value.sluttdato)) return;
        linje.controls.sluttdato.setValue(this.klokke.nåSomPlainDate(this.gruppensTidssone()));
    }

    private somSpesifikasjon(): MedlemStengTilgangSpesifikasjon {
        const avsluttedeRolleperioder = this.aktiveRollerSkjema.value
          .filter(linje => linje.avslutt === true)
          .map(linje => ({
              id: linje.rolleperiode!.id,
              slutt: skrivPlainDateSerialisert(linje.sluttdato!)
          }));

        return {
            oppdaterMoetemal: true,//eksponeres pt ikke i gui
            oppdaterFremtidigeMoeter: true, //eksponeres pt ikke i gui
            avsluttedeRolleperioder: avsluttedeRolleperioder
        };
    }

    private gruppensTidssone() {
        return this.gjeldendeTidssone.gruppensTidssone(this.gruppe.id);
    }
}

export interface DialogParametre {
    medlem: GruppeMedlemListeModell;
    gruppe: EnkelGruppeModell;
}

@Injectable()
export class MedlemStengTilgangDialogKomponentAvhengigheter {

    constructor(private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste) {
    }

    async fjernTilgang(id: MedlemId, spesifikasjon: MedlemStengTilgangSpesifikasjon): Promise<void> {
        await this.gruppemedlemDatatjeneste.fjernTilgang(id, spesifikasjon);
    }
}

function rolleperiodeSkjema(rolle: MedlemsRollePeriode2Modell) {
    let periode = lesDatoPeriodeUtenTid(rolle.periode)!;
    const skjema = new FormGroup({
        rolleperiode: new FormControl(rolle),
        minSluttdato: new FormControl<PlainDate>(periode.start),
        sluttdato: new FormControl<PlainDate | null>(periode.slutt, {validators: Validators.required}),
        avslutt: new FormControl(erIkkeNil(periode.slutt)),
    });
    if (erNil(periode.slutt)) skjema.controls.sluttdato.disable();
    return skjema;
}
